import React, { useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { convertHeicFilesToJpeg, filterOutRawFiles } from '../lib/heicConvert';
import { uploadFilesForMobileSession } from '../lib/mobileUploadSession';
import { useLanguage } from '../context/LanguageContext';
import './MobileUploadPage.css';

// Standalone page opened by scanning the QR code / link from the "Shto nga
// Telefoni" pairing modal in the PhotoBook designer (PagesManager.js). Picks
// photos on the phone and PUTs them straight to Supabase Storage under this
// session's folder; the desktop tab polls and pulls them into its own photo
// library. Deliberately has no Header/Footer/nav - it's a one-purpose relay
// page, not a normal site page.
const MobileUploadPage = () => {
  const { sessionId } = useParams();
  const { t } = useLanguage();
  const fileInputRef = useRef(null);
  const [status, setStatus] = useState('idle'); // idle | uploading | done | error
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [uploadedCount, setUploadedCount] = useState(0);

  const handleFileSelect = async (e) => {
    let files = filterOutRawFiles(Array.from(e.target.files || []));
    e.target.value = '';
    if (files.length === 0) return;

    setStatus('uploading');
    setProgress({ done: 0, total: files.length });

    try {
      const converted = await convertHeicFilesToJpeg(files);
      const { uploaded } = await uploadFilesForMobileSession(sessionId, converted, {
        onProgress: (done, total) => setProgress({ done, total }),
      });
      setUploadedCount(c => c + uploaded);
      setStatus('done');
    } catch (err) {
      console.error('Mobile upload failed:', err);
      setStatus('error');
    }
  };

  return (
    <div className="mup-page">
      <div className="mup-card">
        <h1>{t('mobileUpload.title')}</h1>
        <p className="mup-subtitle">{t('mobileUpload.subtitle')}</p>

        {status === 'uploading' && (
          <div className="mup-progress">
            <div className="mup-spinner" />
            <p>{t('mobileUpload.uploading', { done: progress.done, total: progress.total })}</p>
          </div>
        )}

        {status === 'done' && (
          <div className="mup-success">
            <div className="mup-check">✓</div>
            <p>{t('mobileUpload.done', { count: uploadedCount })}</p>
            <p className="mup-hint">{t('mobileUpload.doneHint')}</p>
          </div>
        )}

        {status === 'error' && (
          <p className="mup-error">{t('mobileUpload.error')}</p>
        )}

        {status !== 'uploading' && (
          <button
            type="button"
            className="mup-add-btn"
            onClick={() => fileInputRef.current?.click()}
          >
            + {status === 'done' ? t('mobileUpload.addMore') : t('mobileUpload.addPhotos')}
          </button>
        )}

        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
      </div>
    </div>
  );
};

export default MobileUploadPage;
